import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Inbox, DollarSign, Search as SearchIcon, CheckSquare,
  Globe, Settings, LogOut, Phone, MessageCircle, FileText, RefreshCw, Star,
} from "lucide-react";
import { api, COMPANY } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { CommandChat } from "../../components/admin/CommandChat";
import { NotificationBell } from "../../components/admin/NotificationBell";
import { ProjectsManager } from "../../components/admin/ProjectsManager";
import { DailyBrief, Research, Social, Finance, Approvals, SettingsScreen } from "./screens";

const NAV = [
  { key: "leads", label: "Leads & Team Ops", icon: Inbox },
  { key: "dashboard", label: "Daily Brief", icon: LayoutDashboard },
  { key: "socials", label: "Socials & Reviews", icon: Star },
  { key: "finance", label: "Finance", icon: DollarSign },
  { key: "research", label: "Research", icon: SearchIcon },
  { key: "approvals", label: "Approvals", icon: CheckSquare },
  { key: "website", label: "Website", icon: Globe },
  { key: "settings", label: "Settings", icon: Settings },
];

const STATUSES = ["new", "contacted", "quoted", "won", "lost"];
const STATUS_STYLE = {
  new: "bg-[#62B848] text-white",
  contacted: "bg-blue-100 text-blue-700",
  quoted: "bg-amber-100 text-amber-700",
  won: "bg-[#224733] text-white",
  lost: "bg-gray-200 text-gray-600",
};

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function WebsiteEditor() {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => { api.get("/admin/site-content/draft").then((r) => setDraft(r.data)).catch((e) => console.error("Failed to load site content draft", e)); }, []);
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const save = async () => {
    setSaving(true); setMsg("");
    try { await api.put("/admin/site-content/draft", draft); setMsg("Draft saved."); }
    catch { setMsg("Save failed."); } finally { setSaving(false); }
  };
  const publish = async () => {
    setSaving(true); setMsg("");
    try { await api.put("/admin/site-content/draft", draft); await api.post("/admin/site-content/publish"); setMsg("Published — live on the website."); }
    catch { setMsg("Publish failed."); } finally { setSaving(false); }
  };

  if (!draft) return <div className="p-12 text-center text-[#4A5550]"><RefreshCw size={24} className="animate-spin mx-auto mb-3" /> Loading content…</div>;
  const cls = "w-full rounded-xl border border-black/10 bg-white px-4 py-2.5 text-sm text-[#1A2421] focus:outline-none focus:ring-2 focus:ring-[#62B848]";
  const label = "text-xs font-bold uppercase tracking-wide text-[#4A5550] mb-1.5 block";

  return (
    <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6">
      <div className="bg-white rounded-3xl border border-black/5 p-6 md:p-8 space-y-5">
        <h2 className="font-display font-extrabold text-2xl text-[#1A2421]">Edit website content</h2>
        <div><label className={label}>Hero headline</label><input data-testid="cms-headline" className={cls} value={draft.hero_headline || ""} onChange={set("hero_headline")} /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={label}>Hero BEFORE image URL</label><input data-testid="cms-before" className={cls} value={draft.hero_before_url || ""} onChange={set("hero_before_url")} /></div>
          <div><label className={label}>Hero AFTER image URL</label><input data-testid="cms-after" className={cls} value={draft.hero_after_url || ""} onChange={set("hero_after_url")} /></div>
        </div>
        <div><label className={label}>Service area text</label><input data-testid="cms-area" className={cls} value={draft.service_area_text || ""} onChange={set("service_area_text")} /></div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div><label className={label}>Radius (km)</label><input type="number" className={cls} value={draft.service_area_radius_km || 20} onChange={set("service_area_radius_km")} /></div>
          <div><label className={label}>Phone</label><input className={cls} value={draft.phone || ""} onChange={set("phone")} /></div>
          <div><label className={label}>Email</label><input className={cls} value={draft.email || ""} onChange={set("email")} /></div>
        </div>
        <div><label className={label}>Address</label><input className={cls} value={draft.address || ""} onChange={set("address")} /></div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" data-testid="cms-reviews-enabled" checked={!!draft.reviews_enabled} onChange={(e) => setDraft((d) => ({ ...d, reviews_enabled: e.target.checked }))} className="w-5 h-5 accent-[#62B848]" />
          <span className="text-sm font-semibold text-[#1A2421]">Show Google reviews section (requires live Google API key)</span>
        </label>
        <div className="flex items-center gap-3 pt-2">
          <button data-testid="cms-save" onClick={save} disabled={saving} className="px-5 py-2.5 rounded-full bg-[#F1F5F0] text-[#224733] font-bold text-sm hover:bg-black/5">Save draft</button>
          <button data-testid="cms-publish" onClick={publish} disabled={saving} className="px-6 py-2.5 rounded-full bg-[#62B848] hover:bg-[#4E9B38] text-white font-bold text-sm">Publish</button>
          {msg && <span className="text-sm font-semibold text-[#224733]">{msg}</span>}
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-black/5 p-6 self-start">
        <h3 className="font-display font-bold text-lg text-[#1A2421] mb-4">Live preview</h3>
        <div className="relative rounded-2xl overflow-hidden h-52 bg-[#1A2421]">
          <img src={draft.hero_before_url} alt="before" className="absolute inset-0 w-full h-full object-cover" />
          <img src={draft.hero_after_url} alt="after" className="absolute inset-0 w-1/2 h-full object-cover border-r-2 border-[#62B848]" />
          <div className="absolute inset-0 bg-black/30 grid place-items-center">
            <span className="font-display font-black heading-up uppercase text-white text-xl text-center px-4">{draft.hero_headline}</span>
          </div>
        </div>
        <p className="text-sm text-[#4A5550] mt-4">{draft.service_area_text}</p>
        <p className="text-xs text-[#4A5550] mt-2">{draft.phone} · {draft.email}</p>
      </div>
    </div>
  );
}

function WebsiteTab() {
  const [sub, setSub] = useState("content");
  const tab = (k, label) => (
    <button data-testid={`website-sub-${k}`} onClick={() => setSub(k)}
      className={`px-5 py-2.5 rounded-full text-sm font-bold transition-colors ${sub === k ? "bg-[#224733] text-white" : "bg-white text-[#224733] border border-black/10 hover:border-[#62B848]"}`}>{label}</button>
  );
  return (
    <div className="space-y-6">
      <div className="flex gap-2">{tab("content", "Site content")}{tab("projects", "Projects")}</div>
      {sub === "content" ? <WebsiteEditor /> : <ProjectsManager />}
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState("leads");
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [focusApproval, setFocusApproval] = useState(null);

  const openApproval = (id) => { setActive("approvals"); setFocusApproval(id || null); };
  const isOwner = user?.role === "owner";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("tab") === "approvals") setActive("approvals");
    const ap = params.get("approval");
    if (ap) setFocusApproval(ap);
  }, []);

  const loadApprovals = useCallback(() => api.get("/approvals/count").then((r) => setPendingApprovals(r.data.pending)).catch((e) => console.error("Failed to load approvals count", e)), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/leads/list");
      setLeads(r.data);
    } catch (e) { console.error("Failed to load leads", e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); loadApprovals(); }, [load, loadApprovals]);
  useEffect(() => { loadApprovals(); }, [active, loadApprovals]);

  const setStatus = async (id, status) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    try { await api.patch(`/leads/${id}/status`, { status }); } catch (e) { console.error("Failed to update lead status", e); }
  };

  const markRead = async (id) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, is_read: true } : l)));
    try { await api.patch(`/leads/${id}/read`); } catch (e) { console.error("Failed to mark lead read", e); }
  };

  const doLogout = async () => { await logout(); navigate("/command", { replace: true }); };

  const unreadCount = leads.filter((l) => !l.is_read).length;
  const filteredRaw = filter === "all" ? leads : leads.filter((l) => l.status === filter);
  const filtered = [...filteredRaw].sort((a, b) => (a.is_read === b.is_read ? 0 : a.is_read ? 1 : -1));
  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: leads.filter((l) => l.status === s).length }), {});

  return (
    <div className="min-h-screen bg-[#F1F5F0] flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-[#1A2421] text-white p-4 fixed inset-y-0">
        <div className="px-2 py-3 mb-4">
          <img src="/logo-full-white.png" alt="West Face Roofing" className="w-44 h-auto" />
          <div className="text-[10px] text-white/50 uppercase tracking-wide mt-2 ml-0.5">Command Center</div>
        </div>
        <nav className="flex-1 space-y-1">
          {NAV.filter((n) => isOwner || n.key !== "settings").map((n) => (
            <button key={n.key} data-testid={`nav-${n.key}`} onClick={() => setActive(n.key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${active === n.key ? "bg-[#62B848] text-white" : "text-white/60 hover:bg-white/5 hover:text-white"}`}>
              <n.icon size={18} /> {n.label}
              {n.key === "approvals" && pendingApprovals > 0 && (
                <span data-testid="approvals-badge" className="ml-auto grid place-items-center min-w-[20px] h-5 px-1.5 rounded-full bg-white text-[#224733] text-[11px] font-bold">{pendingApprovals}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="border-t border-white/10 pt-3">
          <div className="flex items-center gap-3 px-2 py-2">
            {user?.picture ? <img src={user.picture} alt="" className="w-9 h-9 rounded-full" /> : <div className="w-9 h-9 rounded-full bg-[#62B848] grid place-items-center font-bold">{(user?.name || "J")[0]}</div>}
            <div className="leading-tight min-w-0">
              <div className="text-sm font-bold truncate">{user?.name || "Jake"}</div>
              <div className="text-[11px] text-white/50 truncate">{user?.email}</div>
            </div>
          </div>
          <button data-testid="logout-btn" onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-white/60 hover:bg-white/5 hover:text-white transition-colors">
            <LogOut size={18} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-64 xl:ml-[596px]">
        <header className="sticky top-0 z-10 bg-white/85 backdrop-blur-xl border-b border-black/5 px-5 md:px-8 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-display font-extrabold text-xl text-[#1A2421]">{NAV.find((n) => n.key === active)?.label}</h1>
            {active === "leads" && <p className="text-xs text-[#4A5550]">Website quote, PM/strata &amp; chatbot enquiries · queued for ServiceM8</p>}
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell onOpenApproval={openApproval} />
            {active === "leads" && <button data-testid="refresh-leads" onClick={load} className="grid place-items-center w-10 h-10 rounded-full bg-[#F1F5F0] hover:bg-[#62B848] hover:text-white transition-colors"><RefreshCw size={17} className={loading ? "animate-spin" : ""} /></button>}
            <button onClick={doLogout} className="lg:hidden grid place-items-center w-10 h-10 rounded-full bg-[#F1F5F0]"><LogOut size={17} /></button>
          </div>
        </header>

        <div className="p-5 md:p-8">
          {active === "website" ? (
            <WebsiteTab />
          ) : active === "dashboard" ? (
            <DailyBrief />
          ) : active === "socials" ? (
            <Social />
          ) : active === "finance" ? (
            <Finance />
          ) : active === "research" ? (
            <Research />
          ) : active === "approvals" ? (
            <Approvals focusId={focusApproval} onFocusHandled={() => setFocusApproval(null)} />
          ) : active === "settings" && isOwner ? (
            <SettingsScreen onLogout={doLogout} />
          ) : (
            <>
              {unreadCount > 0 && (
                <div data-testid="new-enquiries-banner" className="mb-5 flex items-center gap-3 rounded-2xl bg-[#62B848]/12 border border-[#62B848]/30 px-5 py-3.5">
                  <span className="grid place-items-center w-9 h-9 rounded-full bg-[#62B848] text-white font-display font-black text-sm">{unreadCount}</span>
                  <div className="text-sm text-[#1A2421]"><span className="font-bold">New {unreadCount === 1 ? "enquiry" : "enquiries"}</span> — nothing gets missed. Every website enquiry is emailed to you and saved here.</div>
                </div>
              )}
              {/* Stat cards */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-7">
                <button onClick={() => setFilter("all")} className={`text-left bg-white rounded-2xl border p-5 transition-all ${filter === "all" ? "border-[#62B848] shadow-md" : "border-black/5 hover:shadow-sm"}`}>
                  <div className="font-display font-black text-3xl text-[#1A2421]">{leads.length}</div>
                  <div className="text-xs text-[#4A5550] font-semibold mt-1">All leads</div>
                </button>
                {STATUSES.map((s) => (
                  <button key={s} data-testid={`stat-${s}`} onClick={() => setFilter(s)} className={`text-left bg-white rounded-2xl border p-5 transition-all ${filter === s ? "border-[#62B848] shadow-md" : "border-black/5 hover:shadow-sm"}`}>
                    <div className="font-display font-black text-3xl text-[#1A2421]">{counts[s]}</div>
                    <div className="text-xs text-[#4A5550] font-semibold mt-1 capitalize">{s}</div>
                  </button>
                ))}
              </div>

              {/* Leads list */}
              <div className="bg-white rounded-3xl border border-black/5 overflow-hidden">
                {loading ? (
                  <div className="p-12 text-center text-[#4A5550]"><RefreshCw size={24} className="animate-spin mx-auto mb-3" /> Loading leads…</div>
                ) : filtered.length === 0 ? (
                  <div className="p-16 text-center">
                    <Inbox size={40} className="mx-auto mb-4 text-[#62B848]/50" />
                    <p className="font-display font-bold text-lg text-[#1A2421]">No leads {filter !== "all" ? `in "${filter}"` : "yet"}</p>
                    <p className="text-[#4A5550] text-sm mt-1">Submissions from the website quote form &amp; chat assistant will appear here.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-black/5">
                    {filtered.map((l, i) => (
                      <motion.div key={l.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                        data-testid={`lead-row-${l.id}`} className={`p-5 md:p-6 transition-colors ${!l.is_read ? "bg-[#62B848]/[0.06] border-l-4 border-[#62B848]" : "hover:bg-[#F8F9FA]"}`}>
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2.5 mb-1.5">
                              {!l.is_read && <span data-testid={`lead-new-${l.id}`} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#62B848] text-white">NEW</span>}
                              <span className="font-display font-bold text-lg text-[#1A2421]">{l.name}</span>
                              <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${l.source === "chat" ? "bg-[#62B848]/15 text-[#224733]" : l.source === "pm_strata" ? "bg-purple-50 text-purple-600" : "bg-blue-50 text-blue-600"}`}>
                                {l.source === "chat" ? <MessageCircle size={11} /> : <FileText size={11} />}
                                {l.source === "chat" ? "Chatbot" : l.source === "pm_strata" ? "PM/Strata form" : "Website form"}
                              </span>
                              {l.servicem8_synced
                                ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">ServiceM8 ✓</span>
                                : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Will sync to ServiceM8 when connected</span>}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#4A5550]">
                              <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1.5 font-semibold text-[#224733] hover:text-[#62B848]"><Phone size={13} /> {l.phone}</a>
                              {l.email && <span>{l.email}</span>}
                              {(l.address || l.suburb) && <span className="font-medium text-[#1A2421]">{l.address || l.suburb}</span>}
                              {l.job_type && <span className="font-medium text-[#1A2421]">{l.job_type}</span>}
                              <span className="text-xs">{timeAgo(l.created_at)}</span>
                            </div>
                            {l.message && <p className="text-sm text-[#4A5550] mt-2 line-clamp-2">{l.message}</p>}
                          </div>
                          <div className="flex items-center gap-2">
                            {!l.is_read && <button data-testid={`lead-mark-read-${l.id}`} onClick={() => markRead(l.id)} className="text-xs font-bold px-3 py-2 rounded-full bg-[#224733] text-white hover:bg-[#1A3a29]">Mark as read</button>}
                            <select data-testid={`lead-status-${l.id}`} value={l.status} onChange={(e) => { setStatus(l.id, e.target.value); if (!l.is_read) markRead(l.id); }}
                              className={`text-xs font-bold px-3 py-2 rounded-full border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#62B848] ${STATUS_STYLE[l.status]}`}>
                              {STATUSES.map((s) => <option key={s} value={s} className="bg-white text-[#1A2421]">{s}</option>)}
                            </select>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <CommandChat onAction={() => { load(); loadApprovals(); }} />
    </div>
  );
}
